import React, { useEffect, useState } from "react";
import { Pencil1Icon, TrashIcon, PersonIcon, CopyIcon, DotsHorizontalIcon } from "@radix-ui/react-icons";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@Constants/routes";
import { Switch } from "@Components/shadcn/ui/switch";
import { useUpdateAutomationStatusMutation } from "../../apiSlice";
import { useGetFlowAutomationMutation } from '@Pages/AutomationBuilder/apiSlice';
import { AnalyticsParamBuilder, formatTimestampDifference } from "@Utils/common"
import { displayToast } from "@Utils/toast";
import posthog from "posthog-js";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@Components/shadcn/ui/dropdown-menu";


interface AutomationTableInterface {
    data: Array<any>,
    openDeleteAutomationModal: (id: number) => void,
    fetchAutomations: () => void,
    openLeadsModal: (id: number) => void,
    onCreateAutomation: (e: any) => void
}

const COMPONENT = "automations_list_table"


const AutomationTable = (props: AutomationTableInterface) => {

    const navigate = useNavigate()
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    let [UpdateAutomationStatus, { data: UpdateAutomationStatusData, error: UpdateAutomationStatusError, isLoading: isUpdateAutomationStatusLoading }] = useUpdateAutomationStatusMutation()
    const [getFlowAutomation, { isLoading: isCloning }] = useGetFlowAutomationMutation();

    const handleSwitchOnCheck = (id: number, status: boolean) => {
        UpdateAutomationStatus({
            automation_id: id, status
        })
    }

    useEffect(() => {
        if (UpdateAutomationStatusData) {
            props.fetchAutomations()
        }
    }, [UpdateAutomationStatusData, UpdateAutomationStatusError])

    const OnEditClick = (id: number, ui: string) => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "edit_automation",
            classValue: ui,
            int_value: id
        }));
        navigate(ROUTES.FLOW_BUILDER.replace(':automation_doc_id', `${id}`))
    }

    const OnCloneClick = async (id: number) => {
        try {
            posthog.capture("ui_action", AnalyticsParamBuilder({
                kingdom: COMPONENT,
                phylum: "clone_automation",
                classValue: "clone_icon",
                int_value: id
            }));

            const resp = await getFlowAutomation({ automation_id: id }).unwrap();
            if (resp?.data) {
                const clonedData = {
                    ...resp.data,
                    trigger: {
                        ...resp.data.trigger,
                        selectedMedia: undefined
                    }
                };

                navigate(ROUTES.FLOW_BUILDER.replace(':automation_doc_id', 'clone'), {
                    state: { clonedData }
                });
            }
        } catch (error) {
            console.error('Failed to clone automation:', error);
            displayToast("error", "Failed to clone automation");
        }
    }

    const OnDeleteAutomationClick = (id: number) => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "delete_automation",
            classValue: "delete_bin",
            int_value: id
        }));
        props.openDeleteAutomationModal(id)
    }

    const OnLeadsClick = (id: number) => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "view_leads",
            classValue: "leads_icon",
            int_value: id
        }));
        props.openLeadsModal(id)
    }

    const openImageModal = (imageUrl: string) => {
        if (imageUrl) setSelectedImage(imageUrl);
    }


    return (
        <div className="w-full">
            {/* Desktop Table */}
            <div className="hidden md:block">
                <table className="w-full">
                    <thead className="bg-gray-50/50">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[100px]">Image</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[120px]">Status</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[150px]">Created</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[150px]">Last Modified</th>
                            <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-[140px]">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {props?.data && props?.data.length > 0 ? (
                            props.data.map((automation: any) => (
                                <tr key={automation.id} className="group hover:bg-gray-50/80 transition-all duration-200">
                                    <td className="px-6 py-4">
                                        {automation?.thumbnail_img ? (
                                            <img
                                                className="h-12 w-12 rounded-lg object-cover shadow-sm border border-gray-100 bg-white cursor-pointer hover:opacity-90 transition-opacity"
                                                src={automation?.thumbnail_img}
                                                alt={automation.name}
                                                onClick={() => openImageModal(automation.thumbnail_img)}
                                            />
                                        ) : (
                                            <div className="h-12 w-12 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center">
                                                <svg className="w-6 h-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <p
                                            className="font-medium text-gray-900 group-hover:text-[#3535d5] transition-colors cursor-pointer text-base"
                                            onClick={() => OnEditClick(automation.id, "name_text")}
                                        >
                                            {automation.name}
                                        </p>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${automation?.status
                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                            : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                                            <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${automation?.status ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                                            {automation?.status ? 'Active' : 'Inactive'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <p className="text-sm text-gray-500 font-medium">{formatTimestampDifference(automation.created_at)}</p>
                                    </td>
                                    <td className="px-6 py-4">
                                        <p className="text-sm text-gray-500 font-medium">{formatTimestampDifference(automation.updated_at)}</p>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end items-center gap-1">
                                            <button
                                                onClick={() => OnLeadsClick(automation?.id)}
                                                className="px-3 py-1.5 text-xs font-medium text-[#3535d5] hover:text-[#2b2bb8] bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-100 mr-2"
                                                title="View User Data"
                                            >
                                                Leads Data
                                            </button>
                                            <button
                                                onClick={() => OnEditClick(automation.id, "edit_pencil")}
                                                className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all"
                                                title="Edit Automation"
                                            >
                                                <Pencil1Icon className="w-5 h-5" />
                                            </button>

                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <button className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all outline-none">
                                                        <DotsHorizontalIcon className="w-5 h-5" />
                                                    </button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="w-[160px]">
                                                    <DropdownMenuItem
                                                        onClick={(e) => { e.stopPropagation(); OnCloneClick(automation.id); }}
                                                        disabled={isCloning}
                                                        className="cursor-pointer"
                                                    >
                                                        <CopyIcon className="mr-2 h-4 w-4" />
                                                        <span>Clone</span>
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        onClick={(e) => { e.stopPropagation(); OnDeleteAutomationClick(automation?.id); }}
                                                        className="text-red-600 focus:text-red-600 cursor-pointer"
                                                    >
                                                        <TrashIcon className="mr-2 h-4 w-4" />
                                                        <span>Delete</span>
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={6} className="px-6 py-12 text-center">
                                    <div className="flex flex-col items-center justify-center text-gray-400 py-4">
                                        <svg className="w-16 h-16 mb-4 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                        </svg>
                                        <p className="text-lg font-medium text-gray-900 mb-1">No automations found</p>
                                        <p className="text-sm text-gray-500 mb-6">Get started by creating your first automation.</p>
                                        <button
                                            onClick={props.onCreateAutomation}
                                            className="inline-flex items-center justify-center gap-2 bg-[#3535d5] hover:bg-[#2b2bb8] text-white px-5 py-2.5 rounded-xl font-medium transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="size-4">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                            </svg>
                                            Create Automation
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile Cards (Grid) */}
            <div className="md:hidden p-4 space-y-4">
                {props?.data && props?.data.length > 0 ? (
                    props.data.map((automation: any) => (
                        <div key={`mobile-${automation.id}`} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col gap-4">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    {automation?.thumbnail_img ? (
                                        <img
                                            className="h-14 w-14 rounded-lg object-cover shadow-sm border border-gray-100 bg-gray-50 cursor-pointer hover:opacity-90 transition-opacity"
                                            src={automation?.thumbnail_img}
                                            alt={automation.name}
                                            onClick={() => openImageModal(automation.thumbnail_img)}
                                        />
                                    ) : (
                                        <div className="h-14 w-14 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center">
                                            <svg className="w-6 h-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                    )}
                                    <div>
                                        <h3
                                            className="font-semibold text-gray-900 mb-1 line-clamp-1 text-base"
                                            onClick={() => navigate(ROUTES.FLOW_BUILDER.replace(':automation_doc_id', automation.id))}
                                        >
                                            {automation.name}
                                        </h3>
                                        <div className="flex flex-col items-start gap-1.5">
                                            <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${automation?.status
                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                                                <span className={`w-1 h-1 rounded-full mr-1 ${automation?.status ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                                                {automation?.status ? 'Active' : 'Inactive'}
                                            </div>
                                            <span className="text-xs text-gray-400 font-medium">
                                                Updated {formatTimestampDifference(automation.updated_at)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button className="p-2 -mr-2 text-gray-400 hover:text-gray-700">
                                            <DotsHorizontalIcon className="w-5 h-5" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                            onClick={(e) => { e.stopPropagation(); OnCloneClick(automation.id); }}
                                            disabled={isCloning}
                                        >
                                            <CopyIcon className="mr-2 h-4 w-4" />
                                            <span>Clone</span>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            onClick={(e) => { e.stopPropagation(); OnDeleteAutomationClick(automation?.id); }}
                                            className="text-red-600 focus:text-red-600"
                                        >
                                            <TrashIcon className="mr-2 h-4 w-4" />
                                            <span>Delete</span>
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>

                            <div className="flex items-center border-t border-gray-50 pt-4 mt-2">
                                <div className="flex w-full gap-3">
                                    <button
                                        onClick={() => OnLeadsClick(automation?.id)}
                                        className="flex-1 text-sm font-medium text-[#3535d5] bg-blue-50 border border-blue-100 px-4 py-2.5 rounded-lg justify-center flex"
                                    >
                                        Leads Data
                                    </button>
                                    <button
                                        onClick={() => navigate(ROUTES.FLOW_BUILDER.replace(':automation_doc_id', automation.id))}
                                        className="flex-1 text-sm font-medium text-gray-700 bg-white border border-gray-200 px-4 py-2.5 rounded-lg justify-center flex"
                                    >
                                        Edit
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="text-center py-12 bg-white rounded-xl border border-gray-100 flex flex-col items-center">
                        <p className="text-gray-900 font-medium mb-1">No automations found</p>
                        <p className="text-gray-500 text-sm mb-4">Create your first automation now.</p>
                        <button
                            onClick={props.onCreateAutomation}
                            className="inline-flex items-center justify-center gap-2 bg-[#3535d5] hover:bg-[#2b2bb8] text-white px-5 py-2.5 rounded-xl font-medium transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5 text-sm"
                        >
                            Create Automation
                        </button>
                    </div>
                )}
            </div>

            {/* Image Modal */}
            {selectedImage && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 transition-all duration-300"
                    onClick={() => setSelectedImage(null)}
                >
                    <div className="relative max-w-4xl w-full max-h-[90vh] flex items-center justify-center">
                        <button
                            className="absolute -top-12 right-0 text-white hover:text-gray-300 p-2"
                            onClick={() => setSelectedImage(null)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <img
                            src={selectedImage}
                            alt="Enlarged preview"
                            className="max-w-full max-h-[85vh] rounded-lg shadow-2xl object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

export default AutomationTable